import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { resetSitesCache } from "@/components/dailylogs/DlEditors";

function SiteRow({ s, onSave }) {
  const [name, setName] = useState(s.name);
  const [aliases, setAliases] = useState((s.aliases || []).join(", "));
  return (
    <div className="flex flex-wrap items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-b-0" data-testid={`dl-site-${s.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== s.name && onSave(s.id, { name })} className="h-8 w-48 text-xs" data-testid={`dl-site-name-${s.id}`} />
      <Input value={aliases} onChange={(e) => setAliases(e.target.value)} onBlur={() => aliases !== (s.aliases || []).join(", ") && onSave(s.id, { aliases: aliases.split(",").map((a) => a.trim()).filter(Boolean) })}
        placeholder="Chat nicknames, comma separated (e.g. hungry harbor)" className="h-8 w-72 text-xs" data-testid={`dl-site-aliases-${s.id}`} />
      <span className="text-[10px] text-slate-400">Timesheet: {(s.ts_jobs || []).join(" · ") || "—"} · {s.item_count} items</span>
    </div>
  );
}

function PeopleCard() {
  const [d, setD] = useState({ people: [], roster: [] });
  const load = () => api.get("/dailylogs/people").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (sid, employee) => {
    try { await api.patch(`/dailylogs/people/${encodeURIComponent(sid)}`, { employee }); toast.success("Mapping saved"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid="dl-people-card">
      <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">WhatsApp senders → employees <span className="text-xs font-normal text-slate-400">auto-matched to the Timesheet/Salaries roster; correct any mismatch</span></div>
      {d.people.map((p) => (
        <div key={p.sender_id} className="flex items-center gap-3 px-3 py-1.5 border-b border-slate-100 last:border-b-0 text-xs" data-testid={`dl-person-${p.sender_id}`}>
          <span className="w-48 truncate text-slate-700">{p.sender_name || p.sender_id}</span>
          <select value={p.employee || ""} onChange={(e) => save(p.sender_id, e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs w-48" data-testid={`dl-person-select-${p.sender_id}`}>
            <option value="">— not an employee / unknown —</option>{d.roster.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {p.manual && <span className="text-[9px] uppercase font-bold text-emerald-600">manual</span>}
        </div>
      ))}
    </div>
  );
}

export function SitesCard() {
  const [sites, setSites] = useState([]);
  const load = () => api.get("/dailylogs/sites").then(({ data }) => setSites(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (id, body) => {
    try { await api.patch(`/dailylogs/sites/${id}`, body); resetSitesCache(); toast.success("Site saved — stamps will refresh shortly"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200" data-testid="dl-sites-card">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">Jobsites (from Strata Timesheet) <span className="text-xs font-normal text-slate-400">compact display names, no towns; add chat nicknames to improve matching</span></div>
        {sites.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No Timesheet jobsites found yet.</div> : sites.map((s) => <SiteRow key={s.id} s={s} onSave={save} />)}
      </div>
      <PeopleCard />
    </>
  );
}
